import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { suggestContentIdeas, saveContentIdea, getJobStatus, ContentIdea } from '../../lib/api';
import LanguageSelector from '../../components/LanguageSelector';
import SkeletonIdeaCard from '../../components/SkeletonIdeaCard';

const COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
];

export default function HelpMeWithIdeasPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  const [geo, setGeo] = useState('US');
  const [customKeywords, setCustomKeywords] = useState('');
  const [limit, setLimit] = useState(3); // Default to 3 ideas
  const [language, setLanguage] = useState('en'); // Default to English, will use workspace default if available
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState<ContentIdea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savedIdeas, setSavedIdeas] = useState<Set<string>>(new Set());
  const [savingIdea, setSavingIdea] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const status = await getJobStatus(jobId);
      setJobProgress(status.progress || 0);

      if (status.status === 'completed') {
        // Extract ideas from result
        if (status.result && status.result.ideas) {
          setIdeas((prev) => [...prev, ...status.result.ideas]);
          toast.success(t('create.ideas.generationSuccess'));
        } else {
          setError('No ideas returned from job');
          toast.error(t('create.ideas.generationError'));
        }
        setActiveJobId(null);
        setIsGenerating(false);
      } else if (status.status === 'failed') {
        const errorMsg = status.errorMessage || 'Job failed';
        setError(errorMsg);
        toast.error(t('create.ideas.generationError'));
        setActiveJobId(null);
        setIsGenerating(false);
      }
    } catch (err) {
      console.error('Failed to poll job status:', err);
      setError(err instanceof Error ? err.message : 'Failed to check job status');
      setActiveJobId(null);
      setIsGenerating(false);
    }
  }, [t]);

  // Set up polling interval when job is active
  useEffect(() => {
    if (!activeJobId) return;

    // Poll immediately
    pollJobStatus(activeJobId);

    // Then poll every 3 seconds
    const interval = setInterval(() => {
      pollJobStatus(activeJobId);
    }, 3000);

    return () => clearInterval(interval);
  }, [activeJobId, pollJobStatus]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const keywords = customKeywords
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k.length > 0);

      // Start async job
      const result = await suggestContentIdeas({
        geo,
        keywords: keywords.length > 0 ? keywords : undefined,
        limit,
        language: language || undefined, // Falls back to workspace default on backend
      });

      // Start polling job status
      setActiveJobId(result.jobId);
      setIsGenerating(true);
      setJobProgress(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start ideas generation');
    } finally {
      setLoading(false);
    }
  };

  const handleUseIdea = (idea: ContentIdea) => {
    // Navigate to create from idea page with pre-filled idea
    navigate(`/${workspaceSlug}/create?type=idea`, {
      state: {
        idea: idea.description,
        title: idea.title,
        platforms: idea.suggestedPlatforms,
      },
    });
  };

  const handleSaveIdea = async (idea: ContentIdea) => {
    setSavingIdea(idea.id);

    try {
      const keywords = customKeywords
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k.length > 0);

      await saveContentIdea(idea, {
        geo,
        keywords: keywords.length > 0 ? keywords : undefined,
      });

      setSavedIdeas((prev) => new Set(prev).add(idea.id));
      toast.success(t('create.ideas.saveSuccess'));
    } catch (err) {
      toast.error(t('create.ideas.saveError'));
    } finally {
      setSavingIdea(null);
    }
  };

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high':
        return 'text-green-600 dark:text-green-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'low':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600';
    }
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, string> = {
      linkedin: '💼',
      instagram: '📷',
      facebook: '📘',
      twitter: '🐦',
      youtube: '▶️',
      tiktok: '🎵',
      pinterest: '📌',
    };
    return icons[platform] || '📱';
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('create.backToDashboard')}
        </button>
        <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">{t('create.ideas.title')}</h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">{t('create.ideas.subtitle')}</p>
      </div>

      {/* Input Form */}
      <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Country Selector */}
          <div>
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.ideas.countryLabel')}
            </label>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              For Google Trends data (not output language)
            </p>
            <select
              value={geo}
              onChange={(e) => setGeo(e.target.value)}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
              disabled={loading}
            >
              {COUNTRY_OPTIONS.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          {/* Number of Ideas */}
          <div>
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.ideas.limitLabel')}
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
              disabled={loading}
            />
          </div>

          {/* Custom Keywords */}
          <div>
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.ideas.keywordsLabel')} <span className="text-gray-500 dark:text-gray-400 font-normal text-sm">({t('common.optional')})</span>
            </label>
            <input
              type="text"
              value={customKeywords}
              onChange={(e) => setCustomKeywords(e.target.value)}
              placeholder={t('create.ideas.keywordsPlaceholder')}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
              disabled={loading}
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {t('create.ideas.keywordsHint')}
            </p>
          </div>
        </div>

        {/* Language Selection */}
        <div className="mb-6">
          <LanguageSelector
            value={language}
            onChange={setLanguage}
            label="Output Language"
            description="Choose the language for AI-generated content ideas"
            disabled={loading}
          />
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="w-full px-6 py-3 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white font-bold text-lg rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('create.ideas.generating')}
            </span>
          ) : (
            t('create.ideas.generateButton')
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-8">
          <p className="text-red-800 dark:text-red-300 font-bold">{error}</p>
        </div>
      )}

      {/* Progress Bar during generation */}
      {isGenerating && (
        <div className="mb-8 bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5 text-gray-900 dark:text-white" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="font-bold text-lg dark:text-gray-100">Generating ideas...</span>
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">{jobProgress}%</span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-black dark:bg-white transition-all duration-300 ease-out"
              style={{ width: `${jobProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Ideas Grid (includes both skeleton and real ideas) */}
      {(ideas.length > 0 || isGenerating) && (
        <div>
          <h2 className="text-2xl font-bold mb-6 dark:text-gray-100">{t('create.ideas.resultsTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Skeleton cards during generation */}
            {isGenerating && Array.from({ length: limit }).map((_, index) => (
              <SkeletonIdeaCard key={`skeleton-${index}`} />
            ))}

            {/* Real idea cards */}
            {ideas.map((idea) => (
              <div
                key={idea.id}
                className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6"
              >
                {/* Engagement Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`text-xs font-bold uppercase px-2 py-1 border-2 border-black dark:border-gray-600 rounded ${getEngagementColor(idea.estimatedEngagement)}`}
                  >
                    {t(`create.ideas.engagement.${idea.estimatedEngagement}`)}
                  </span>
                  {idea.trendContext && (
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      🔥 {idea.trendContext.traffic || `${idea.trendContext.relevanceScore.toFixed(1)}/10`}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold mb-3 dark:text-gray-100">{idea.title}</h3>

                {/* Description */}
                <p className="text-gray-700 dark:text-gray-300 mb-4">{idea.description}</p>

                {/* Trend Context */}
                {idea.trendContext && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-3 mb-4">
                    <p className="text-sm text-yellow-900 dark:text-yellow-300">
                      <span className="font-bold">{t('create.ideas.trendingTopic')}:</span>{' '}
                      {idea.trendContext.topic}
                    </p>
                  </div>
                )}

                {/* Platforms */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {idea.suggestedPlatforms.map((platform) => (
                    <span
                      key={platform}
                      className="inline-flex items-center px-3 py-1 border-2 border-black dark:border-gray-600 rounded text-sm font-bold dark:text-gray-100"
                    >
                      <span className="mr-1">{getPlatformIcon(platform)}</span>
                      {t(`platforms.${platform}`)}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleSaveIdea(idea)}
                    disabled={savedIdeas.has(idea.id) || savingIdea === idea.id}
                    className={`flex-1 px-4 py-2 border-2 border-black dark:border-gray-600 font-bold rounded transition-colors ${
                      savedIdeas.has(idea.id)
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 cursor-not-allowed'
                        : savingIdea === idea.id
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-wait'
                        : 'bg-white dark:bg-gray-800 text-black dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {savingIdea === idea.id
                      ? t('create.ideas.saving')
                      : savedIdeas.has(idea.id)
                      ? t('create.ideas.saved')
                      : t('create.ideas.saveButton')}
                  </button>
                  <button
                    onClick={() => handleUseIdea(idea)}
                    className="flex-1 px-4 py-2 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white font-bold rounded hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    {t('create.ideas.useIdeaButton')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !isGenerating && ideas.length === 0 && !error && (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">💡</div>
          <h3 className="text-xl font-bold mb-2 dark:text-gray-100">{t('create.ideas.emptyTitle')}</h3>
          <p className="text-gray-600 dark:text-gray-400">{t('create.ideas.emptyDescription')}</p>
        </div>
      )}
    </div>
  );
}
